import { create } from 'zustand';
import { ordersService } from '../services/ordersService';
import { startOfDay, subDays, format, parseISO, subHours } from 'date-fns';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { campaignServiceInstance as campaignService } from '../services/campaignService';
import { nekoPos } from 'public-contracts';

interface DashboardStats {
  todayRevenue: number;
  todayOrders: number;
  todayCustomers: number;
  pendingOrders: number;
  averageOrderValue: number;
  // Growth percentages compared to yesterday
  revenueGrowth: number;
  ordersGrowth: number;
  customersGrowth: number;
  // Goal progress percentages
  revenueGoalProgress: number;
  ordersGoalProgress: number;
  // Performance metrics
  avgProcessingTime: number;
  satisfactionRate: number;
  ordersPerHour: number;
  // Campaign performance metrics (when active campaign exists)
  campaignAOV?: number;
  campaignAOVTarget?: number;
  campaignRepeatPurchaseRate?: number;
  campaignRepeatPurchaseTarget?: number;
  campaignNewCustomerRate?: number;
  campaignNewCustomerTarget?: number;
  topSellingProducts: Array<{
    name: string;
    quantity: number;
    revenue: number;
  }>;
  recentOrders: Array<{
    id: string;
    customer: string;
    amount: number;
    status: string;
    time: string;
  }>;
  hourlySales: Array<{
    hour: string;
    revenue: number;
    orders: number;
  }>;
}

interface DashboardState {
  stats: DashboardStats;
  isLoading: boolean;
  error: string | null;
  isStaffView: boolean; // Flag to indicate if data is filtered for staff (last 6 hours)
  fetchDashboardData: (userRole?: string) => Promise<void>;
}

const calculateOrderTotal = (order: any): number => {
  if (order.total) return order.total;
  if (order.items && Array.isArray(order.items)) {
    return order.items.reduce((sum: number, item: any) => {
      return sum + item.price * item.quantity;
    }, 0);
  }
  return 0;
};

const getCustomerName = (order: any): string => {
  // First check for explicit customer name
  if (order.customer?.name) return order.customer.name;
  if (order.customerName) return order.customerName;

  // Check if there's a customerId (could be string, number, or object with id)
  const customerId = order.customerId || order.customer?.id;

  // If there's a customerId but no name, it's an unregistered user
  if (customerId) {
    // Check if customerId looks like an ID (not empty, not just whitespace)
    const idString = String(customerId).trim();
    if (idString && idString.length > 0) {
      return 'Unregistered user';
    }
  }

  return 'pos.dashboard.default_customer';
};

const getProductName = (item: any): string => {
  if (item.product?.name) return item.product.name;
  if (item.name) return item.name;
  return 'pos.dashboard.default_product';
};

const getTimeAgo = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return 'pos.dashboard.time_just_now';
  if (diffInMinutes < 60)
    return `${diffInMinutes}|pos.dashboard.time_minutes_ago`;
  if (diffInMinutes < 1440)
    return `${Math.floor(diffInMinutes / 60)}|pos.dashboard.time_hours_ago`;
  return `${Math.floor(diffInMinutes / 1440)}|pos.dashboard.time_days_ago`;
};

export const useDashboardStore = create<DashboardState>((set) => ({
  stats: {
    todayRevenue: 0,
    todayOrders: 0,
    todayCustomers: 0,
    pendingOrders: 0,
    averageOrderValue: 0,
    revenueGrowth: 0,
    ordersGrowth: 0,
    customersGrowth: 0,
    revenueGoalProgress: 0,
    ordersGoalProgress: 0,
    avgProcessingTime: 0,
    satisfactionRate: 0,
    ordersPerHour: 0,
    topSellingProducts: [],
    recentOrders: [],
    hourlySales: [],
  },
  isLoading: false,
  error: null,
  isStaffView: false,

  fetchDashboardData: async (userRole?: string) => {
    try {
      set({ isLoading: true, error: null });

      // Check if user is staff (not storeManager or above)
      const isStaff = userRole === 'staff' || userRole === 'cashier';
      const isManagerOrStaff = isStaff || userRole === 'storeManager';
      set({ isStaffView: isStaff });

      // For staff, only show last 6 hours of data
      const sixHoursAgo = subHours(new Date(), 6);
      const today = isStaff ? sixHoursAgo : startOfDay(new Date());
      const yesterday = isStaff ? subHours(sixHoursAgo, 24) : subDays(startOfDay(new Date()), 1);
      const lastWeek = subDays(startOfDay(new Date()), 7);

      // Fetch today's orders (excluding cancelled orders)
      const todayOrdersResponse = (await ordersService.getOrders({
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'date', order: 'DESC' },
        filter: {
          dateFrom: today.toISOString(),
          dateTo: new Date().toISOString(),
          // Exclude cancelled orders from dashboard
          status: { $ne: 'cancelled' } as any,
        },
      })) as { data?: any[] };

      // Fetch yesterday's orders for comparison (excluding cancelled orders)
      const yesterdayOrdersResponse = (await ordersService.getOrders({
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'date', order: 'DESC' },
        filter: {
          dateFrom: yesterday.toISOString(),
          dateTo: today.toISOString(),
          // Exclude cancelled orders from dashboard
          status: { $ne: 'cancelled' } as any,
        },
      })) as { data?: any[] };

      // Fetch recent orders for analysis (excluding cancelled orders)
      const recentOrdersResponse = (await ordersService.getOrders({
        pagination: { page: 1, perPage: 50 },
        sort: { field: 'date', order: 'DESC' },
        filter: {
          dateFrom: lastWeek.toISOString(),
          dateTo: new Date().toISOString(),
          // Exclude cancelled orders from dashboard
          status: { $ne: 'cancelled' } as any,
        },
      })) as { data?: any[] };

      // Filter out cancelled orders client-side as well (safety measure)
      let todayOrders = (todayOrdersResponse.data || []).filter(
        (order: any) => order.status !== 'cancelled'
      );
      const yesterdayOrders = (yesterdayOrdersResponse.data || []).filter(
        (order: any) => order.status !== 'cancelled'
      );
      let recentOrders = (recentOrdersResponse.data || []).filter(
        (order: any) => order.status !== 'cancelled'
      );

      // For staff, filter orders to last 6 hours only
      if (isStaff) {
        todayOrders = todayOrders.filter((order: any) => {
          const orderDate = new Date(order.date || order.createdAt);
          return orderDate >= sixHoursAgo;
        });
        recentOrders = recentOrders.filter((order: any) => {
          const orderDate = new Date(order.date || order.createdAt);
          return orderDate >= sixHoursAgo;
        });
      }

      // Calculate today's stats
      const todayRevenue = todayOrders.reduce((sum: number, order: any) => {
        return sum + calculateOrderTotal(order);
      }, 0);

      const todayCustomers = new Set(
        todayOrders.map((order: any) => order.customerId || order.customer?.id)
      ).size;

      const pendingOrders = todayOrders.filter(
        (order: any) => order.status === 'ordered' || order.status === 'pending'
      ).length;

      const averageOrderValue =
        todayOrders.length > 0 ? todayRevenue / todayOrders.length : 0;

      // Calculate growth percentages compared to yesterday (or previous 6 hours for staff)
      let yesterdayRevenue = 0;
      let yesterdayOrdersCount = 0;
      let yesterdayCustomers = 0;

      if (isStaff) {
        // For staff, compare with previous 6 hours (6-12 hours ago)
        const twelveHoursAgo = subHours(new Date(), 12);
        const sixHoursAgo = subHours(new Date(), 6);
        const previousPeriodOrders = (todayOrdersResponse.data || []).filter(
          (order: any) => {
            if (order.status === 'cancelled') return false;
            const orderDate = new Date(order.date || order.createdAt);
            return orderDate >= twelveHoursAgo && orderDate < sixHoursAgo;
          }
        );
        yesterdayRevenue = previousPeriodOrders.reduce(
          (sum: number, order: any) => sum + calculateOrderTotal(order),
          0
        );
        yesterdayOrdersCount = previousPeriodOrders.length;
        yesterdayCustomers = new Set(
          previousPeriodOrders.map(
            (order: any) => order.customerId || order.customer?.id
          )
        ).size;
      } else {
        yesterdayRevenue = yesterdayOrders.reduce(
          (sum: number, order: any) => {
            return sum + calculateOrderTotal(order);
          },
          0
        );
        yesterdayOrdersCount = yesterdayOrders.length;
        yesterdayCustomers = new Set(
          yesterdayOrders.map(
            (order: any) => order.customerId || order.customer?.id
          )
        ).size;
      }

      const revenueGrowth =
        yesterdayRevenue > 0
          ? ((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100
          : 0;
      const ordersGrowth =
        yesterdayOrdersCount > 0
          ? ((todayOrders.length - yesterdayOrdersCount) /
              yesterdayOrdersCount) *
            100
          : 0;
      const customersGrowth =
        yesterdayCustomers > 0
          ? ((todayCustomers - yesterdayCustomers) / yesterdayCustomers) * 100
          : 0;

      // Calculate goal progress from active campaign
      let revenueGoal = 10000000; // Default: 10M VND daily goal
      let ordersGoal = 100; // Default: 100 orders daily goal
      
      try {
        // Fetch active campaign
        const campaigns = await campaignService.getCampaigns();
        const activeCampaign = campaigns.find(
          (c: nekoPos.Campaign) => 
            c.isActive && 
            new Date(c.startDate) <= new Date() && 
            new Date(c.endDate) >= new Date()
        );
        
        if (activeCampaign && activeCampaign.targetRevenue) {
          // Calculate daily revenue goal from campaign's total target revenue
          const campaignStart = new Date(activeCampaign.startDate);
          const campaignEnd = new Date(activeCampaign.endDate);
          const campaignDurationDays = Math.max(
            1,
            Math.ceil((campaignEnd.getTime() - campaignStart.getTime()) / (1000 * 60 * 60 * 24))
          );
          
          // Daily revenue goal = total target revenue / campaign duration in days
          revenueGoal = activeCampaign.targetRevenue / campaignDurationDays;
          
          // Calculate orders goal based on average order value
          if (averageOrderValue > 0) {
            // Use average order value from today's orders
            ordersGoal = Math.ceil(revenueGoal / averageOrderValue);
          } else {
            // Fallback: estimate 100,000 VND per order if no order data available
            ordersGoal = Math.ceil(revenueGoal / 100000);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch active campaign for goals:', error);
        // Use default values if campaign fetch fails
      }
      
      const revenueGoalProgress =
        revenueGoal > 0 ? Math.min((todayRevenue / revenueGoal) * 100, 100) : 0;
      const ordersGoalProgress =
        ordersGoal > 0
          ? Math.min((todayOrders.length / ordersGoal) * 100, 100)
          : 0;

      // Calculate performance metrics
      // TODO: Calculate from actual order timestamps when available
      const avgProcessingTime = 0; // Will be calculated from order timestamps
      const satisfactionRate = 0; // Will come from ratings API when available
      const ordersPerHour =
        todayOrders.length > 0
          ? todayOrders.length / 8 // Assuming 8-hour workday
          : 0;

      // Calculate campaign-specific performance metrics
      let campaignAOV: number | undefined;
      let campaignAOVTarget: number | undefined;
      let campaignRepeatPurchaseRate: number | undefined;
      let campaignRepeatPurchaseTarget: number | undefined;
      let campaignNewCustomerRate: number | undefined;
      let campaignNewCustomerTarget: number | undefined;

      // Get active campaign metadata for performance targets
      try {
        const campaigns = await campaignService.getCampaigns();
        const activeCampaign = campaigns.find(
          (c: nekoPos.Campaign) => 
            c.isActive && 
            new Date(c.startDate) <= new Date() && 
            new Date(c.endDate) >= new Date()
        );

        if (activeCampaign) {
          // Calculate AOV (Average Order Value) - already calculated above
          campaignAOV = averageOrderValue;
          
          // Get AOV target from campaign metadata
          if (activeCampaign.metadata?.targetAOVIncrease) {
            // targetAOVIncrease is the increase amount, so target = current baseline + increase
            // For now, use the increase as a target to reach
            campaignAOVTarget = averageOrderValue + activeCampaign.metadata.targetAOVIncrease;
          }

          // Calculate repeat purchase rate
          // This would need historical data - for now, calculate from today's orders
          const uniqueCustomers = new Set(
            todayOrders.map((order: any) => order.customerId || order.customer?.id)
          ).size;
          const repeatCustomers = todayCustomers - uniqueCustomers;
          campaignRepeatPurchaseRate = todayCustomers > 0 
            ? repeatCustomers / todayCustomers 
            : 0;
          
          // Get repeat purchase target from campaign metadata
          if (activeCampaign.metadata?.targetRepeatPurchaseRate !== undefined) {
            campaignRepeatPurchaseTarget = activeCampaign.metadata.targetRepeatPurchaseRate;
          }

          // Calculate new customer rate
          // Compare today's customers to yesterday's to estimate new customers
          const newCustomersEstimate = Math.max(0, todayCustomers - yesterdayCustomers);
          campaignNewCustomerRate = todayCustomers > 0 
            ? newCustomersEstimate / todayCustomers 
            : 0;
          
          // Get new customer target from campaign metadata
          if (activeCampaign.metadata?.targetNewCustomerIncrease !== undefined) {
            // targetNewCustomerIncrease is a percentage increase
            campaignNewCustomerTarget = activeCampaign.metadata.targetNewCustomerIncrease / 100;
          }
        }
      } catch (error) {
        console.warn('Failed to fetch campaign metadata for performance metrics:', error);
      }

      // Calculate top selling products
      const productStats = new Map<
        string,
        {
          quantity: number;
          revenue: number;
          name: string;
          productId: string | number;
        }
      >();

      recentOrders.forEach((order: any) => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach((item: any) => {
            const productId = item.productId;
            if (!productId) return; // Skip items without productId

            const quantity = item.quantity || 0;
            const revenue = (item.price || 0) * quantity;

            const productIdStr = String(productId);
            if (productStats.has(productIdStr)) {
              const existing = productStats.get(productIdStr)!;
              existing.quantity += quantity;
              existing.revenue += revenue;
            } else {
              productStats.set(productIdStr, {
                quantity,
                revenue,
                name: getProductName(item), // Fallback name
                productId: productId,
              });
            }
          });
        }
      });

      const topSellingProductsUnsorted = Array.from(productStats.values())
        .filter((product) => product.quantity > 0) // Only include products that were actually sold
        .sort((a, b) => {
          // Sort by quantity first (most sold), then by revenue as tiebreaker
          if (b.quantity !== a.quantity) {
            return b.quantity - a.quantity;
          }
          return b.revenue - a.revenue;
        })
        .slice(0, 5);

      // Fetch product names from API
      const topSellingProducts = await Promise.all(
        topSellingProductsUnsorted.map(async (product) => {
          try {
            const response = await authenticatedFetch(
              `/api/products/${product.productId}`
            );
            if (response.ok) {
              const productData = await response.json();
              return {
                ...product,
                name: productData.name || product.name,
              };
            }
          } catch (error) {
            console.warn(
              `Failed to fetch product ${product.productId}:`,
              error
            );
          }
          return product; // Return with fallback name if fetch fails
        })
      );

      // Calculate hourly sales for today (or last 6 hours for staff)
      const hoursToShow = isStaff ? 6 : 24;
      const hourlySales = Array.from({ length: hoursToShow }, (_, index) => {
        let hourStart: Date;
        let hourEnd: Date;
        
        if (isStaff) {
          // For staff, show last 6 hours
          const currentHour = new Date().getHours();
          const targetHour = currentHour - 5 + index; // Last 6 hours: currentHour-5 to currentHour
          hourStart = new Date();
          hourStart.setHours(targetHour, 0, 0, 0);
          hourStart.setMinutes(0, 0, 0);
          hourEnd = new Date(hourStart);
          hourEnd.setHours(targetHour, 59, 59, 999);
        } else {
          // For managers, show all 24 hours of today
          hourStart = new Date(today);
          hourStart.setHours(index, 0, 0, 0);
          hourEnd = new Date(hourStart);
          hourEnd.setHours(index, 59, 59, 999);
        }

        const hourOrders = todayOrders.filter((order: any) => {
          const orderDate = new Date(order.date || order.createdAt);
          return orderDate >= hourStart && orderDate <= hourEnd;
        });

        const hourRevenue = hourOrders.reduce((sum: number, order: any) => {
          return sum + calculateOrderTotal(order);
        }, 0);

        return {
          hour: format(hourStart, 'HH:mm'),
          revenue: hourRevenue,
          orders: hourOrders.length,
        };
      }).filter((hour) => hour.revenue > 0 || hour.orders > 0);

      // Filter recent orders to only show orders that need attention for storeManager/staff
      // Orders that need attention: 'ordered' or 'ready' (not 'delivered' or 'cancelled')
      const ordersNeedingAttention = isManagerOrStaff
        ? recentOrders.filter(
            (order: any) =>
              order.status === 'ordered' ||
              order.status === 'ready' ||
              order.status === 'pending' ||
              order.status === 'preparing'
          )
        : recentOrders;

      // Format recent orders
      const formattedRecentOrders = ordersNeedingAttention
        .slice(0, 5)
        .map((order: any) => ({
          id: order.id || order._id,
          customer: getCustomerName(order),
          amount: calculateOrderTotal(order),
          status: order.status || 'pending',
          time: getTimeAgo(order.date || order.createdAt),
        }));

      set({
        stats: {
          todayRevenue,
          todayOrders: todayOrders.length,
          todayCustomers,
          pendingOrders,
          averageOrderValue,
          revenueGrowth,
          ordersGrowth,
          customersGrowth,
          revenueGoalProgress,
          ordersGoalProgress,
          avgProcessingTime,
          satisfactionRate,
          ordersPerHour,
          campaignAOV,
          campaignAOVTarget,
          campaignRepeatPurchaseRate,
          campaignRepeatPurchaseTarget,
          campaignNewCustomerRate,
          campaignNewCustomerTarget,
          topSellingProducts,
          recentOrders: formattedRecentOrders,
          hourlySales,
        },
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      set({
        error: 'Failed to load dashboard data',
        isLoading: false,
      });
    }
  },
}));
